import { useState, useMemo } from 'react';
import { Grid, Box, Button, Divider, Stack } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useQuery } from 'react-query';
import { IResponse, IPunchlistTemplate } from '../../../../models';
import { getPunchlistTemplates, deletePunchlistTemplate } from '../../../../fetch';
import { Page, PageTitle, SearchField, SearchSection, Table, Pagination } from '../../../../components';
import { PunchlistTemplateModal } from '../../checklist-templates/PunchlistTemplateModal';

export const PunchlistTemplates = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isPunchlistTemplateModalOpen, setIsPunchlistTemplateModalOpen] = useState<boolean>(false);
  const [currentPunchlistTemplate, setCurrentPunchlistTemplate] = useState<IPunchlistTemplate>({
    punchlistTemplateId: undefined,
    name: '',
    description: '',
    numberOfToDoItems: 0,
    punchlistItems: [],
  })
  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    Name?: 'Asc' | 'Desc';
    Description?: 'Asc' | 'Desc';
    NumberOfItems?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSearchedValue('');
    setPage(0);
  };
  const { isLoading: isLoadingTemplates, data: templatesData, refetch } = useQuery<IResponse<IPunchlistTemplate[]>, Error>(
    ['punchlist-templates', page, perPage, searchedValue, selectedSort, sortDirection],
    () =>
      getPunchlistTemplates({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        searchText: searchedValue
      }),
    {
      onSuccess: (d: any) => {
        const data: IResponse<IPunchlistTemplate[]> = d;
        setRecordCount(data.totalRecordCount);
        return d;
      },
      onError: (e: any) => {
        // make sure the call doesn't refresh again if we hit an error
        enqueueSnackbar(e?.Detail || `Error loading punchlist templates.`, {
          variant: 'error'
        });
      },
      notifyOnChangeProps: 'tracked',
    },
  );
  const templates = useMemo(() => templatesData?.records ?? [], [templatesData]);

  const handleEdit = async (punchlistTemplate) => {
    setCurrentPunchlistTemplate(punchlistTemplate);
    setIsPunchlistTemplateModalOpen(true);
  };

  const handleDelete = async id => {
    const result = window.confirm('Are you sure you want to delete this punchlist template?');
    if (result) {
      setIsDeleting(true);
      try {
        await deletePunchlistTemplate(id);
        enqueueSnackbar(`Punchlist template deleted!`, {
          variant: 'success'
        });
        await refetch();
      } catch (error) {
        enqueueSnackbar('Error deleting punchlist template, please refresh and try again.', {
          variant: 'error'
        });
        console.log(error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const tableDataColumns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Description',
        accessor: 'description',
        isServerSorted: selectedSort === 'Description',
        isServerSortedDesc: sortDirection.Description === 'Desc',
        handleClickColumn: () => handleClickColumn('Description')
      },
      {
        Header: 'Num. To-Do Items',
        accessor: 'numberOfToDoItems',
        isServerSorted: selectedSort === 'NumberOfItems',
        isServerSortedDesc: sortDirection.NumberOfItems === 'Desc',
        handleClickColumn: () => handleClickColumn('NumberOfItems')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistTemplate } };
        }) => {
          return (
            <Box textAlign="right">
              <Button
                id="edit"
                color="secondary"
                startIcon={<Edit />}
                onClick={() => {
                  handleEdit(original);
                }}
              >
                Edit
              </Button>
              <Button
                sx={theme => ({
                  marginLeft: theme.spacing(0.5),
                  color: theme.palette.error.main
                })}
                startIcon={<Delete />}
                onClick={() => handleDelete(original.punchlistTemplateId)}
              >
                Delete
              </Button>
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, selectedSort, sortDirection]);

  return (
    <Page title="Punchlist Templates">
      <Stack justifyContent="space-between" flexDirection={{ xs: 'column', sm: 'row' }}>
        <Box>
          <PageTitle showDivider={false} title="Punchlist Templates" />
        </Box>
        <Box mb={2}>
          <Button
            id="add-punchlist"
            fullWidth
            onClick={() => {
              setIsPunchlistTemplateModalOpen(true);
            }}
            startIcon={<Add />}
            color="primary"
            variant="contained"
          >
            Add Template
          </Button>
        </Box>
      </Stack>
      <Box mb={2}>
        <Divider />
      </Box>
      <Box mb={1}>
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={12} sm={6} md={4}>
            <SearchField
              isDisabled={false}
              handleSearch={(val: string) => {
                setPage(0);
                setSearchedValue(val);
              }}
              placeholder="Search by name or description..."
              handleClearSearch={handleClearSearch}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />
          </Grid>
        </Grid>
      </Box>
      <Box mb={1}>{searchedValue && <SearchSection searchedValue={searchedValue} handleClearSearch={handleClearSearch} />}</Box>
      <Table columns={tableDataColumns} data={templates} isLoading={isLoadingTemplates || isDeleting} hidePagination />
      {!isLoadingTemplates && templates?.length > 0 && <Pagination page={page} count={recordCount} rowsPerPage={perPage} setPage={setPage} setRowsPerPage={setRowsPerPage} />}
      <PunchlistTemplateModal
        currentPunchlistTemplate={currentPunchlistTemplate}
        fetchPunchlistTemplates={refetch}
        open={isPunchlistTemplateModalOpen}
        onClose={() => {
          setCurrentPunchlistTemplate(undefined);
          setIsPunchlistTemplateModalOpen(false);
        }}
      />
    </Page>
  );
};
